import React, { Fragment, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import BoundaryError from 'common/components/BoundaryError'
import { Chip, Grid, Icon, Stack, Typography } from '@mui/material'
import useFetch from 'common/hooks/useFetch'
import ApplicationConf from 'conf/ApplicationConf'
import { getTextColorByHexColor, POLES_COLORS } from 'common/utils/ColorUtil'
import MenuIcon from '@mui/icons-material/Menu'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Cancel, Clear, Clock, DataBankStatus, Delete, Download, Edit, New, Other, Refresh, Save, Sound, Upload } from 'common/components/action/Action'
import useBoolean from 'common/hooks/useBoolean'
import aquasysLogoPath from 'assets/pictures/logoaQuasys.png'
import aquaparkPng from 'assets/pictures/aquapark.png'
import { CANCEL, CLEAR, CLOCK, DATA_BANK_STATUS, DELETE, DOWNLOAD, EDIT, NEW, OTHER, REFRESH, SAVE, SOUND, UPLOAD } from 'common/components/action/ActionConstant'
import { darkBlue, mainBlue } from 'app/theme'
import { hasPermission } from 'common/utils/HabilitationUtil'
import Psykokwalypse from 'common/components/psyduck/Psykokwalypse'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { logout } from 'app/appSlice'
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer'
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball'
import soundTadeo from 'assets/sound/tadeo.m4a'
import soundTadeo2 from 'assets/sound/tadeo2.m4a'
import soundNewTicketAlert from 'assets/sound/mipmip.mp3'
import useSound from './hooks/useSound'

// délais de 1 minute entre chaque refresh
const SUPPORT_REFRESH_INTERVAL_MS = 60 * 1000

// eslint-disable-next-line no-unused-vars
const Tadeo = () => {
    const [playTadeo] = useSound(soundTadeo)
    const [playTadeo2] = useSound(soundTadeo2)
    return (
        <>
            <SportsSoccerIcon
                sx={{
                    fontSize: '38px',
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                }}
                onClick={playTadeo}
            />
            <SportsBasketballIcon
                sx={{
                    fontSize: '38px',
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                }}
                onClick={playTadeo2}
            />
        </>
    )
}

const AppBar = ({
    openNav = () => {},
}) => {
    const navigate = useNavigate()

    const {
        titles,
        actions,
    } = useSelector(store => ({
        titles: store.app.titles,
        actions: store.app.actions,
    }), shallowEqual)

    // be carefull when changing the height, you should update the css in AquaparcApp at the same time
    return (
        <Grid container columnSpacing={3} style={{ backgroundColor: darkBlue, height: '64px', padding: '0 20px' }} alignItems='center'>
            <Grid size='auto'>
                <MenuIcon sx={{ fontSize: '42px', color: 'white', cursor: 'pointer' }} onClick={openNav}/>
            </Grid>
            <Grid container size='auto' spacing={0.5} alignItems='center'>
                {titles.map(({ label = '', href = '' }, i) => {
                    const labelStyle = i !== titles.length - 1 ?
                        { color: 'rgba(255,255,255,0.7)', fontWeight: 'normal' }
                        : { color: 'white', fontWeight: 'bold' }

                    return (
                        <Fragment key={i}>
                            {i > 0 && (
                                <Grid size='auto'>
                                    <ChevronRightIcon fontSize='medium' sx={{ color: 'rgba(255,255,255,0.7)', padding: '0 4' }} />
                                </Grid>
                            )}
                            <Grid size='auto' onClick={() => navigate(href)} sx={{ cursor: 'pointer' }}>
                                <Typography sx={{ ...labelStyle, fontSize: '1rem' }}>{label}</Typography>
                            </Grid>
                        </Fragment>
                    )
                })}
            </Grid>
            <Grid size='grow' />
            <Grid container size='auto' spacing={2} alignItems='center'>
                {actions.map(({ type, ...props }, i) => (
                    <Grid key={i} size='auto'>
                        {type === OTHER && (<Other {...props} />)}
                        {type === NEW && (<New {...props} />)}
                        {type === EDIT && (<Edit {...props} />)}
                        {type === CANCEL && (<Cancel {...props} />)}
                        {type === DELETE && (<Delete {...props} />)}
                        {type === SAVE && (<Save {...props} />)}
                        {type === CLOCK && (<Clock />)}
                        {type === CLEAR && (<Clear {...props} />)}
                        {type === UPLOAD && (<Upload {...props} />)}
                        {type === DOWNLOAD && (<Download {...props} />)}
                        {type === SOUND && (<Sound {...props} />)}
                        {type === REFRESH && (<Refresh {...props} />)}
                        {type === DATA_BANK_STATUS && (<DataBankStatus {...props} />)}
                    </Grid>
                ))}
                <Psykokwalypse />
                {/* <Tadeo /> */}
            </Grid>
        </Grid>
    )
}

AppBar.propTypes = {
    openNav: PropTypes.func,
}

const SlideNavLink = ({
    name = '',
    onClick = () => {},
    icon = '',
    badgeContent,
}) => (
    <Grid
        sx={{
            padding: '4px 4px 4px 10px',
            cursor: 'pointer',
        }}
        onClick={onClick}
    >
        <Grid
            sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                padding: '4px 12px 4px 6px',
                borderRadius: '10px',
                '&:hover': {
                    backgroundColor: 'lightgrey',
                },
            }}
        >
            <Icon sx={{ color: '#777', fontSize: '24px' }}>{icon}</Icon>
            <span style={{ paddingLeft: '5px', color: 'rgba(0,0,0,0.87)' }}>{name}</span>
            {!!badgeContent && (
                <Chip
                    label={badgeContent}
                    size='small'
                    sx={{
                        marginLeft: 1,
                        backgroundColor: mainBlue,
                        color: getTextColorByHexColor(mainBlue),
                    }}
                />
            )}
        </Grid>
    </Grid>
)

SlideNavLink.propTypes = {
    name: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    icon: PropTypes.string.isRequired,
    badgeContent: PropTypes.number,
}

const SlideNav = ({
    isOpen = false,
    close = () => {},
}) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { user, isNewTicketAlertMute } = useSelector(store => ({
        user: store.app.user,
        isNewTicketAlertMute: store.app.isNewTicketAlertMute,
    }), shallowEqual)

    const monitoringModules = [
        { id: 'dashboardProd', name: 'Tableau de bord Prod', icon: 'monitoring', onClick: () => navigate('/dashboardProd') },
        { id: 'instances', name: 'Instances', icon: 'devices', onClick: () => navigate('/instance') },
        { id: 'timeErrors', name: 'Time Errors', icon: 'running_with_errors', onClick: () => navigate('/timeError') },
        { id: 'statisticsSupervision', name: 'Statistiques', icon: 'ssid_chart', onClick: () => navigate('/statisticsSupervision') },
    ].filter(mod => hasPermission(user, mod.id, 'view'))

    const hasSupportAccess = hasPermission(user, 'dashboardSupport', 'view')
    const { data: issuesNotCovered, reload: reloadIssuesNotCovered } = useFetch(hasSupportAccess ? ApplicationConf.support.issuesNotCovered() : null)

    const reloadRef = useRef()
    reloadRef.current = reloadIssuesNotCovered

    useEffect(() => {
        if (!hasSupportAccess) return undefined
        const interval = setInterval(() => reloadRef.current?.(), SUPPORT_REFRESH_INTERVAL_MS)
        return () => clearInterval(interval)
    }, [hasSupportAccess])

    const previousIssueIdsRef = useRef(null)
    const [playNewTicketAlert] = useSound(soundNewTicketAlert)

    useEffect(() => {
        if (!issuesNotCovered) return
        const currentIds = issuesNotCovered.map(ticket => ticket.id)
        if (previousIssueIdsRef.current !== null) {
            const hasNewTicket = currentIds.some(id => !previousIssueIdsRef.current.includes(id))
            if (hasNewTicket && !isNewTicketAlertMute) {
                playNewTicketAlert()
            }
        }
        previousIssueIdsRef.current = currentIds
    }, [issuesNotCovered, isNewTicketAlertMute, playNewTicketAlert])

    const getLeftBarStyle = (color) => ({
        backgroundImage: `-webkit-linear-gradient(left, ${color}, ${color} 3%, transparent 3%, transparent 100%)`,
    })

    const supportModules = [
        { id: 'dashboardSupport', name: 'Tableau de bord Support', icon: 'dashboard', badgeContent: issuesNotCovered?.length, onClick: () => navigate('/support/dashboard') },
        { id: 'issue', name: 'Création de tickets', icon: 'help', onClick: () => navigate('/issue') },
        { id: 'statistics', name: 'Statistiques', icon: 'bar_chart', onClick: () => navigate('/statistics') },
    ].filter(mod => hasPermission(user, mod.id, 'view'))

    const devModules = [
        { id: 'tickets', name: 'Mes tickets', icon: 'assignment_turned_in', onClick: () => navigate(`/tickets/${user.loginMantis}`) },
        { id: 'devs', name: 'Suivi des lots', icon: 'logo_dev', onClick: () => navigate('/devs') },
        { id: 'recette', name: 'Recette', icon: 'receipt_long', onClick: () => navigate('/recette/dashboard') },
        { id: 'roadmap', name: 'Roadmap', icon: 'event', onClick: () => navigate('/roadmap/priorites') },
    ].filter(mod => hasPermission(user, mod.id, 'view'))

    const gdlModules = [
        { id: 'customers', name: 'Clients', icon: 'person', onClick: () => navigate('/customer') },
        { id: 'settingLicenses', name: 'Paramètrage licences', icon: 'style', onClick: () => navigate('/settingLicense') },
        { id: 'commercial', name: 'Commerce', icon: 'shopping_cart', onClick: () => navigate('/commercial') },
    ].filter(mod => hasPermission(user, mod.id, 'view'))

    const adminModules = [
        { id: 'users', name: 'Utilisateurs', icon: 'people', onClick: () => navigate('/user') },
    ].filter(mod => hasPermission(user, mod.id, 'view'))

    return (
        <div
            style={{
                visibility: isOpen ? 'visible' : 'hidden',
                height: '100%',
                width: '100%',
                position: 'fixed',
                backgroundColor: isOpen ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)',
                transition: 'background-color 0.3s',
                top: '0',
                bottom: '0',
                left: '0',
                right: '0',
                zIndex: 9999,
            }}
            onClick={close}
        >
            <div
                style={{
                    height: '100%',
                    left: isOpen ? '0' : '-240px',
                    width: '240px',
                    transition: 'all 0.2s ease-out',
                    backgroundColor: 'white',
                    position: 'fixed',
                    top: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                }}
            >
                <Stack direction='column' justifyContent='space-between' sx={{ minHeight: '100%' }}>
                    <Stack>
                        <div
                            style={{
                                backgroundImage: `url('${aquaparkPng}')`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                width: 176,
                                padding: '15px 32px',
                                backgroundColor: 'rgb(255 255 255 / 35%)',
                                backgroundBlendMode: 'soft-light',
                            }}
                        >
                            <Grid container spacing={0.5}>
                                <Grid size={12}>
                                    <img
                                        src={user.picture}
                                        alt=''
                                        width='50%'
                                        style={ { borderRadius: '15%' } }
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <span style={{ fontWeight: 'bold', color: 'black', cursor: 'default', textDecoration: 'none' }}>
                                        <b style={{ fontSize: 15 }}>{ user.login }</b>
                                    </span>
                                </Grid>
                                <Grid size={12}>
                                    <span style={{ fontWeight: 'bold', color: 'black', cursor: 'default', textDecoration: 'none', fontSize: 12 }}>
                                        { user.email }
                                    </span>
                                </Grid>
                            </Grid>
                        </div>
                        <Grid sx={{ borderBottom: 'solid 1px #CCC' }}>
                            <SlideNavLink onClick={() => navigate('/')} icon='home' name='Accueil' />
                            <SlideNavLink onClick={() => navigate('/agenda')} icon='calendar_month' name='Agenda' />
                        </Grid>
                        {monitoringModules.length > 0 && (
                            <Grid
                                container
                                sx={{
                                    borderBottom: 'solid 1px #CCC',
                                    ...getLeftBarStyle(POLES_COLORS.PROD),
                                }}
                                direction='column'
                            >
                                {monitoringModules.map((mod, i) => (
                                    <Grid key={i}>
                                        <SlideNavLink onClick={mod.onClick} icon={mod.icon} name={mod.name} />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                        {supportModules.length > 0 && (
                            <Grid
                                container
                                sx={{
                                    borderBottom: 'solid 1px #CCC',
                                    ...getLeftBarStyle(POLES_COLORS.SUPPORT),
                                }}
                                direction='column'
                            >
                                {supportModules.map((mod, i) => (
                                    <Grid key={i}>
                                        <SlideNavLink onClick={mod.onClick} icon={mod.icon} name={mod.name} badgeContent={mod.badgeContent} />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                        {devModules.length > 0 && (
                            <Grid
                                container
                                sx={{
                                    borderBottom: 'solid 1px #CCC',
                                    ...getLeftBarStyle(POLES_COLORS.DEV),
                                }}
                                direction='column'
                            >
                                {devModules.map((mod, i) => (
                                    <Grid key={i}>
                                        <SlideNavLink onClick={mod.onClick} icon={mod.icon} name={mod.name} />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                        {gdlModules.length > 0 && (
                            <Grid
                                container
                                sx={{
                                    borderBottom: 'solid 1px #CCC',
                                    ...getLeftBarStyle(POLES_COLORS.COMMERCE),
                                }}
                                direction='column'
                            >
                                {gdlModules.map((mod, i) => (
                                    <Grid key={i}>
                                        <SlideNavLink onClick={mod.onClick} icon={mod.icon} name={mod.name} />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                        {adminModules.length > 0 && (
                            <Grid
                                container
                                sx={{
                                    borderBottom: 'solid 1px #CCC',
                                    ...getLeftBarStyle(POLES_COLORS.ADMIN),
                                }}
                                direction='column'
                            >
                                {adminModules.map((mod, i) => (
                                    <Grid key={i}>
                                        <SlideNavLink onClick={mod.onClick} icon={mod.icon} name={mod.name} />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Stack>
                    <Stack direction='column'>
                        <Grid sx={{ borderBottom: 'solid 1px #CCC', borderTop: 'solid 1px #CCC' }}>
                            <SlideNavLink onClick={() => dispatch(logout())} icon='power_settings_new' name='Déconnexion' />
                        </Grid>
                        <Stack spacing={0.3} margin='20 0'>
                            <Grid container alignItems='center' justifyContent='center'>
                                <a href='https://www.aquasys.fr/' target='_blank'>
                                    <img
                                        src={aquasysLogoPath}
                                        alt=''
                                        width='40px'
                                    />
                                </a>
                            </Grid>
                            <Grid container alignItems='center' justifyContent='center'>
                                v.{process.env.REACT_APP_VERSION}
                            </Grid>
                            <Grid container alignItems='center' justifyContent='center'>
                                <b>©2023 Aquasys</b>
                            </Grid>
                        </Stack>
                    </Stack>
                </Stack>
            </div>
        </div>
    )
}

SlideNav.propTypes = {
    isOpen: PropTypes.bool,
    close: PropTypes.func,
}

const AquaparcApp = () => {
    const { pathname } = useLocation()
    const {
        value: isNavOpen,
        setTrue: openNav,
        setFalse: closeNav,
    } = useBoolean(false)

    return (
        <>
            <AppBar openNav={openNav} />
            <SlideNav isOpen={isNavOpen} close={closeNav} />
            <BoundaryError url={pathname}>
                <div
                    style={{
                        backgroundColor: '#e7eaef',
                        height: 'calc(100vh - 64px)',
                        overflowY: 'auto',
                        // seul endroit qui gère l'espacement avec le bord de page : les pages enfants n'ont pas de marge extérieure
                        padding: '10px',
                        boxSizing: 'border-box',
                    }}
                >
                    <Outlet />
                </div>
            </BoundaryError>
        </>
    )
}

export default AquaparcApp